/**
 * @module components/pages/taxonomymanager
 * @description
 * Taxonomy Manager page - provides a tree-based editor for managing hierarchical taxonomy
 * data used across the application. Features include:
 * - Hierarchical tree rendering with expand/collapse
 * - Debounced search and highlight of matched nodes
 * - Inline add / edit / delete actions for nodes
 * - Tag display and quick actions
 * - Audit log fetching and save functionality via admin services
 *
 * Notes:
 * - Uses a lightweight `TreeNode` component (memoized) for efficient renders.
 * - `useDebounce` custom hook is included to debounce the search input.
 * - State is split across local hooks to keep responsibilities clear.
 */
import React, { useState, useMemo, useCallback, memo,useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Search, ChevronDown, ChevronRight, Plus, Edit3, Filter, Layers, ListChecks, Tag, SearchCheck, CalendarClock, User, CheckCircle2, ArrowLeft } from 'lucide-react';
import {fetchTaxonomyData, saveTaxonomyData,getRecentTaxonomyAuditLogs} from "../../services/admin"
import $ from "jquery";
import { API_SUCCESS_RESPONSE_CODE,API_SUCCESS_RESPONSE_CREATED_CODE } from "../../utils/config_mapping";
import PageLoader from '../common/pageloader';
import CommonIcon from '../common/customicon';
import Message from "../common/message";
import * as ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { useNavigate, useLocation } from "react-router-dom";
import { hasPageAccess,accessDenied,AccessDeniedModal,validatePageAccess } from "../../utils/page_access"; // Import


/**
 * useDebounce - custom hook to debounce a rapidly-updating value.
 * @param {any} value - The value to debounce (e.g. search input)
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {any} debouncedValue - Stable value that updates only after delay
 * @example
 * const debounced = useDebounce(searchTerm, 300);
 */
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * TreeNode - memoized recursive component for rendering a taxonomy node and its children.
 *
 * @param {Object} props
 * @param {Object} props.item - Taxonomy item ({id,label,level,parentId,tags})
 * @param {Array} props.children - Child nodes
 * @param {Set} props.expandedNodes - Set of expanded node ids
 * @param {Function} props.onToggleNode - Toggle expansion handler (id) => void
 * @param {Function} props.onEditItem - Edit handler (item) => void
 * @param {Function} props.onDeleteItem - Delete handler (item) => void
 * @param {Function} props.onAddChildItem - Add child handler (parent) => void
 * @param {string} props.searchTerm - Current debounced search term for highlighting
 * @param {boolean} props.highlightUntagged - Whether to visually highlight untagged nodes
 * @returns {JSX.Element}
 */
const TreeNode = memo(({
  item,
  children,
  expandedNodes,
  onToggleNode,
  onEditItem,
  onDeleteItem,
  onAddChildItem,
  searchTerm,
  highlightUntagged
}) => {
  const hasChildren = children && children.length > 0;
  const isExpanded = expandedNodes.has(item.id);
  const isHighlighted = searchTerm && item.label.toLowerCase().includes(searchTerm.toLowerCase());
  const isUntagged = !item.tags || item.tags.length === 0;

   const [data, setData] = useState([]);

   
  const getIndentLevel = useCallback(() => {
    return (item.level - 1) * 5;
  }, [item.level]);

  const handleToggle = useCallback(() => {
    onToggleNode(item.id);
  }, [onToggleNode, item.id]);

  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    onEditItem(item);
  }, [onEditItem, item]);

  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    onDeleteItem(item);
  }, [onDeleteItem, item]);

    

  const handleAddChild = useCallback(() => {
    onAddChildItem(item);
  }, [onAddChildItem, item]);

  return (
    <div>
      <div 
        className={`d-flex align-items-center py-2 px-3 border-bottom position-relative ${
          isHighlighted ? 'bg-warning bg-opacity-25' : ''
        } ${highlightUntagged && isUntagged ? 'bg-danger bg-opacity-10 border-danger' : ''}`}
        style={{ paddingLeft: `${16 + getIndentLevel()}px`, transition: 'background 0.2s' }}
      >
        {hasChildren ? (
          <button
            className="btn btn-link p-0 me-2 text-muted d-flex align-items-center justify-content-center"
            onClick={handleToggle}
            style={{
              width: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: `${(item.level - 1) * 20}px`
            }}
            tabIndex={-1}
          >
            {isExpanded ? (
              <ChevronDown size={16} style={{ verticalAlign: "middle" }} />
            ) : (
              <ChevronRight size={16} style={{ verticalAlign: "middle" }} />
            )}
          </button>
        ) : (
          <div
            style={{
              width: "18px",
              marginLeft: `${(item.level - 1) * 20}px`
            }}
          />
        )}

        <div className="flex-grow-1 d-flex align-items-center" style={{minHeight:'22px'}}>
          <div className="d-flex flex-column">
            <span className="me-2 d-flex align-items-center" style={{fontSize:"15px", height:'18px'}}>{item.label}</span>
            {item.tags && item.tags.length > 0 && (
              (() => {
                const [showAllTags, setShowAllTags] = React.useState(false);
                const visibleTags = showAllTags ? item.tags : item.tags.slice(0, 5);
                return (
                  <div className="d-flex flex-row gap-1 flex-wrap overflow-auto align-items-center mt-1">
                    {visibleTags.map((tag, index) => (
                      <span key={index} className="badge bg-primary" style={{ fontSize: '11px' }}>
                        {tag.name}
                      </span>
                    ))}
                    {item.tags.length > 5 && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-1"
                        style={{ fontSize: '11px', textDecoration: 'underline' }}
                        onClick={e => { e.stopPropagation(); setShowAllTags(v => !v); }}
                      >
                        {showAllTags ? 'Less' : `More (${item.tags.length - 1})`}
                      </button>
                    )}
                  </div>
                );
              })()
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {hasChildren && (
            <span className="badge bg-light text-dark" style={{ fontSize: '15px' }}>
              ({children.length})
            </span>
          )}
          <button
            className="btn btn-link p-0 text-success"
            onClick={handleAddChild}
            style={{ width: '16px', height: '16px' }}
            title="Add Child"
          >
            <Plus size={14} />
          </button>
          <button
            className="btn btn-link p-0 text-danger"
            onClick={handleDelete}
            style={{ width: '16px', height: '16px' }}
            title="Delete"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" /></svg>
          </button>
          <button
            className="btn btn-link p-0 text-muted"
            onClick={handleEdit}
            style={{ width: '16px', height: '16px' }}
          >
            <Edit3 size={14} />
          </button>
        </div>
      </div>

      {isExpanded && hasChildren && (
        <div>
          {children.map(child => (
            <TreeNode
              key={child.id}
              item={child}
              children={child.children || []}
              expandedNodes={expandedNodes}
              onToggleNode={onToggleNode}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              onAddChildItem={onAddChildItem}
              searchTerm={searchTerm}
              highlightUntagged={highlightUntagged}
            />
          ))}
        </div>
      )}
    </div>
  );
});

TreeNode.displayName = 'TreeNode';

const TaxonomyManager = () => {
  let pageName = "Taxonomy Management";
  const [accessModal, setAccessModal] = useState({ isOpen: false });

  const navigate = useNavigate();
  const location = useLocation();
  const lastLocationRef = React.useRef(location);

  const [taxonomyState, setTaxonomyState] = useState({});
  const [activeTab, setActiveTab] = useState('product');
  const [searchTerm, setSearchTerm] = useState('');

  // Reset search term when tab changes
  React.useEffect(() => {
    setSearchTerm('');
  }, [activeTab]);
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [autoOrganize, setAutoOrganize] = useState(true);
  const [isAddMode, setIsAddMode] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [highlightUntagged, setHighlightUntagged] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [wasEditModalOpen, setWasEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(true); // State to track loading
  const [showTree, setShowTree] = useState(true); // State to toggle tree visibility
  const [validationErrors, setValidationErrors] = useState({}); // State for validation errors
  // Prevent body scrolling when modal or delete confirm is open to avoid double scrollbar
  useEffect(() => {
    const modalOpen = showModal || showDeleteConfirm;
    if (modalOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.dataset._overflow = originalOverflow;
      document.body.style.overflow = 'hidden';
    } else {
      if (document.body.dataset._overflow !== undefined) {
        document.body.style.overflow = document.body.dataset._overflow;
        delete document.body.dataset._overflow;
      } else {
        document.body.style.overflow = '';
      }
    }
    return () => {
      if (document.body.dataset._overflow !== undefined) {
        document.body.style.overflow = document.body.dataset._overflow;
        delete document.body.dataset._overflow;
      } else {
        document.body.style.overflow = '';
      }
    };
  }, [showModal, showDeleteConfirm]);

  /**
   * validateLabel - validate a category/label before saving
   * @param {string} label - Candidate label text
   * @param {string|null} parentId - Parent id of the node being validated
   * @param {string|null} currentId - Current node id (used to exclude self when checking duplicates)
   * @returns {Object} errors - Object with validation error messages (empty when valid)
   */
  const validateLabel = (label, parentId, currentId) => {
    const errors = {};

    // Check for empty
    if (!label.trim()) {
      errors.label = 'Category name cannot be empty.';
      return errors;
    }

    // Check length (max 100 characters)
    if (label.length > 100) {
      errors.label = 'Category name cannot exceed 100 characters.';
      return errors;
    }

    // Special characters: allow but warn for certain ones
    const dangerousChars = /[<>"/\\|?*\x00-\x1f]/;
    if (dangerousChars.test(label)) {
      errors.label = 'Category name contains invalid characters. Please avoid < > " / \\ | ? * and control characters.';
      return errors;
    }

    // Check for duplicates at the same level
    const currentTaxonomy = getCurrentTaxonomy();
    const siblings = currentTaxonomy.filter(item => 
      item.parentId === parentId && item.id !== currentId
    );
    const duplicate = siblings.find(item => 
      item.label.toLowerCase() === label.toLowerCase()
    );
    if (duplicate) {
      errors.label = 'A category with this name already exists at the same level.';
      return errors;
    }

    return errors;
  };

  /**
   * fetchData - loads taxonomy data from the admin service endpoint and transforms it
   * into the internal shape expected by the UI.
   * @async
   * @returns {Promise<void>}
   */
  const fetchData = async () => {
    try {
      const result = await fetchTaxonomyData();
      console.log("API Response:", result); // Debug: log the full API response
      if (result.code === API_SUCCESS_RESPONSE_CODE) {
        // Transform API response to match expected format
        const transformTags = (tags) => {
          if (!tags) return [];
          return tags.map(tag => {
            if (typeof tag === 'string') {
              return { id: tag, name: tag };
            }
            return tag;
          });
        };

        const transformedData = {
          product: deduplicateTaxonomies(result.data.product_taxonomies || []),
          sales: deduplicateTaxonomies(result.data.sales_taxonomies || []),
          territories: deduplicateTaxonomies(result.data.territories_taxonomies || [])
        };

        // Helper function to deduplicate taxonomies with same ID and merge children
        function deduplicateTaxonomies(taxonomies) {
          const idMap = new Map();
          const result = [];

          taxonomies.forEach(item => {
            const transformedItem = {
              ...item,
              tags: transformTags(item.tags)
            };

            if (idMap.has(item.id)) {
              // If we already have this ID, merge children if they exist
              const existingItem = idMap.get(item.id);
              // For root nodes with same ID, we keep the first occurrence
              // The children should be properly linked via parentId relationships
              return;
            }

            idMap.set(item.id, transformedItem);
            result.push(transformedItem);
          });

          return result;
        }
        console.log("Transformed data:", transformedData); // Debug: log transformed data
        setTaxonomyState(transformedData);
        setLoading(false); // Stop loading spinner
      } else {
        console.warn("Unexpected response status:", result.status);
        setLoading(false); // Stop loading spinner even on error
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false); // Stop loading spinner on error
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * getCurrentTaxonomy - returns the taxonomy array for the currently active tab
   * @returns {Array<Object>} taxonomy array for `activeTab`
   */
  const getCurrentTaxonomy = useCallback(() => {
    return taxonomyState[activeTab] || [];
  }, [taxonomyState, activeTab]);

  // Build optimized data structures - memoized
  const { idToItem, childrenMap, rootItems, filteredItems } = useMemo(() => {
    const currentTaxonomy = getCurrentTaxonomy();
    const idToItem = {};
    const childrenMap = {};

    // Build lookup maps
    currentTaxonomy.forEach(item => {
      idToItem[item.id] = item;
      if (item.parentId) {
        if (!childrenMap[item.parentId]) childrenMap[item.parentId] = [];
        childrenMap[item.parentId].push(item);
      }
    });

    let filteredItems = currentTaxonomy;
    
    // Apply search filter if needed
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase();
      const visibleIds = new Set();
    
      // Find matching items and their ancestors
      currentTaxonomy.forEach(item => {
        const matchLabel = item.label.toLowerCase().includes(term);
        const matchTag = item.tags?.some(tag => tag.name.toLowerCase().includes(term));
        
        if (matchLabel || matchTag) {
          visibleIds.add(item.id);
          
          // Add all ancestors
          let current = item;
          while (current && current.parentId) {
            visibleIds.add(current.parentId);
            current = idToItem[current.parentId];
          }
        }
      });
      
      filteredItems = currentTaxonomy.filter(item => visibleIds.has(item.id));
    }

    // Get root items from filtered items
    const rootItems = filteredItems.filter(item => !item.parentId || item.level === 1);
    
    return { idToItem, childrenMap, rootItems, filteredItems };
  }, [getCurrentTaxonomy, debouncedSearchTerm]);

  // Build hierarchical tree structure for rendering - memoized
  const treeStructure = useMemo(() => {
    // Prevent infinite recursion by tracking visited node ids
    const buildTree = (parentId = null, visited = new Set()) => {
      const children = filteredItems.filter(item => (item.parentId == null && parentId == null) || item.parentId === parentId);
      return children.map(item => {
        if (visited.has(item.id)) {
          // Cycle detected, break recursion
          return { ...item, children: [] };
        }
        const newVisited = new Set(visited);
        newVisited.add(item.id);
        return {
          ...item,
          children: buildTree(item.id, newVisited)
        };
      });
    };
    return buildTree(null);
  }, [filteredItems]);

  /**
   * handleToggleNode - toggle expanded/collapsed state for a node id
   * @param {string} id - Node id to toggle
   * @returns {void}
   */
  const handleToggleNode = useCallback((id) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  /**
   * handleExpandAll - expand all nodes in the current taxonomy
   * @returns {void}
   */
  const handleExpandAll = useCallback(() => {
    const allIds = new Set(getCurrentTaxonomy().map(item => item.id));
    setExpandedNodes(allIds);
  }, [getCurrentTaxonomy]);

  /**
   * handleCollapseAll - collapse all nodes
   * @returns {void}
   */
  const handleCollapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // Stats calculations - memoized
  const stats = useMemo(() => {
    const currentTaxonomy = getCurrentTaxonomy();
    const totalCount = currentTaxonomy.length;
    const untaggedCount = currentTaxonomy.filter(item => !item.tags || item.tags.length === 0).length;
    const searchResults = debouncedSearchTerm ? filteredItems.length : 0;
    
    return { totalCount, untaggedCount, searchResults };
  }, [getCurrentTaxonomy, filteredItems.length, debouncedSearchTerm]);

  /**
   * handleEditItem - Open edit modal for a given taxonomy item after permission check
   * @param {Object} item - Taxonomy item to edit
   * @returns {void}
   */
  const handleEditItem = useCallback((item) => {

    const accessCheck = validatePageAccess(pageName, "edit", "You don't have permission to edit category.");
    if (accessCheck) {
      setAccessModal(accessCheck);
      return;
    }

    setSelectedItem(item);
    setValidationErrors({});
    setIsAddMode(false);
    setShowModal(true);
  }, []);

  /**
   * handleDeleteItem - Initiate deletion flow for a taxonomy item (shows confirm)
   * @param {Object} item - Taxonomy item to delete
   * @returns {void}
   */
  const handleDeleteItem = useCallback((item) => {

    const accessCheck = validatePageAccess(pageName, "delete", "You don't have permission to delete category.");
    if (accessCheck) {
      setAccessModal(accessCheck);
      return;
    }

    setItemToDelete(item);
    setWasEditModalOpen(showModal);
    setShowModal(false);
    setShowDeleteConfirm(true);
    //setUnsavedChanges(true);
  }, [showModal]);

  /**
   * handleAddChildItem - Start creating a new child under `parent`
   * @param {Object} parent - Parent taxonomy node
   * @returns {void}
   */
  const handleAddChildItem = useCallback((parent) => {

    const accessCheck = validatePageAccess(pageName, "add", "You don't have permission to add a new category.");
    if (accessCheck) {
      setAccessModal(accessCheck);
      return;
    }

    const nextLevel = Math.min((parent.level || 1) + 1, 3);
    const newItem = {
      id: `new_${Date.now()}`,
      label: 'New Category',
      level: nextLevel,
      parentId: parent.id,
      position: { x: 0, y: 0 },
      tags: []
    };
    setSelectedItem(newItem);
    setIsAddMode(true);
    setShowModal(true);
    //setUnsavedChanges(true);
  }, []);

  const handleAddTopLevel = useCallback(() => {
    const accessCheck = validatePageAccess(pageName, "add", "You don't have permission to add a new category.");
    if (accessCheck) {
      setAccessModal(accessCheck);
      return;
    }
    const newItem = {
      id: `new_${Date.now()}`,
      label: '',
      level: 1,
      position: { x: 0, y: 0 },
      tags: []
    };
    setSelectedItem(newItem);
    setIsAddMode(true);
    setShowModal(true);
   // setUnsavedChanges(true);
  }, []);

  // Recursive delete helper
  const deleteItemById = useCallback((id, arr) => {
    for (let i = arr.length - 1; i >= 0; i--) {
      if (arr[i].id === id) {
        arr.splice(i, 1);
      } else if (arr[i].parentId === id) {
        deleteItemById(arr[i].id, arr);
      }
    }
  }, []);

  /**
   * confirmDelete - actually remove the pending `itemToDelete` from the current taxonomy
   * @returns {void}
   */
  const confirmDelete = useCallback(() => {
    if (itemToDelete) {
      setTaxonomyState(prev => {
        const arr = [...(prev[activeTab] || [])];
        deleteItemById(itemToDelete.id, arr);
        return { ...prev, [activeTab]: arr };
      });
      setItemToDelete(null);
      setShowDeleteConfirm(false);
      setShowModal(false);
      setUnsavedChanges(true);
    }
  }, [itemToDelete, activeTab, deleteItemById]);

  /**
   * cancelDelete - cancel the delete confirmation dialog and restore prior modal state
   * @returns {void}
   */
  const cancelDelete = useCallback(() => {
    setItemToDelete(null);
    setShowDeleteConfirm(false);
    if (wasEditModalOpen) setShowModal(true);
  }, [wasEditModalOpen]);

  /**
   * handleSave - Persist taxonomy changes to server via `saveTaxonomyData`.
   * @async
   * @returns {Promise<void>}
   */
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      // Transform taxonomyState back to API format
      const apiData = {
        product_taxonomies: taxonomyState.product || [],
        sales_taxonomies: taxonomyState.sales || [],
        territories_taxonomies: taxonomyState.territories || []
      };

      const result = await saveTaxonomyData(apiData);
      const targetElement = document.getElementById("app_err");

      if (result.code === API_SUCCESS_RESPONSE_CODE) {
        setUnsavedChanges(false);
       // alert('Changes saved successfully!');
         if (targetElement) {
              // Use React 18 createRoot API for rendering
              if (targetElement._rootContainer) {
                targetElement._rootContainer.unmount();
              }
              const root = createRoot(targetElement);
              root.render(<Message type='success' message={"Changes saved successfully!"} />);
              targetElement._rootContainer = root;
            }
            ReactDOM.render(<Message type='success' message={"Changes saved successfully!"} />, targetElement);
            fetchAuditLog();
        
      } else {
        alert('Failed to save changes. Please try again.');


        if (targetElement) {
              // Use React 18 createRoot API for rendering
              if (targetElement._rootContainer) {
                targetElement._rootContainer.unmount();
              }
              const root = createRoot(targetElement);
              root.render(<Message type='error' message={"Failed to save changes. Please try again."} />);
              targetElement._rootContainer = root;
            }
            ReactDOM.render(<Message type='error' message={"Failed to save changes. Please try again."} />, targetElement);
      }
      
    } catch (error) {
      console.error('Error saving taxonomy data:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [taxonomyState]);

  /**
   * getTabLabel - user-facing label for tabs
   * @param {string} tab - tab key ('product'|'sales'|'territories')
   * @returns {string} human-readable label
   */
  const getTabLabel = useCallback((tab) => {
    switch (tab) {
      case 'product': return 'Product Categories';
      case 'sales': return 'Sales Channels';
      case 'territories': return 'Territories';
      default: return tab;
    }
  }, []);

  // Height of any global/top header (adjust if your layout changes)
  const HEADER_OFFSET = 0; // set to e.g. 64 if there's a fixed app header above

  // Navigation warning for unsaved changes
   React.useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // required for Chrome
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [unsavedChanges]);

  React.useEffect(() => {
    if (!unsavedChanges) {
      lastLocationRef.current = location; // update safely
      return;
    }

    // detect URL change inside app
    if (lastLocationRef.current.pathname !== location.pathname) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave this page?"
      );

      if (!confirmLeave) {
        // cancel navigation → push back previous URL
        navigate(lastLocationRef.current.pathname, { replace: true });
      } else {
        // allow navigation
        lastLocationRef.current = location;
      }
    }
  }, [location, unsavedChanges, navigate]);



  // State for audit log info
  const [auditLog, setAuditLog] = useState(null);

  /**
   * fetchAuditLog - retrieve recent taxonomy audit logs for display in the header
   * @async
   * @returns {Promise<void>}
   */
  async function fetchAuditLog() {
      try {
        const result = await getRecentTaxonomyAuditLogs();
        if (result && result.success && result.data) {
          setAuditLog(result.data);
        }
      } catch (err) {
        setAuditLog(null);
      }
    }

  // Fetch audit log info on mount
  useEffect(() => {
    
    fetchAuditLog();
  }, []);

  return (
    <div className={`main-content${(showModal || showDeleteConfirm) ? ' modal-open' : ''}`} style={{ fontFamily: 'Inter, Segoe UI, Arial, sans-serif', background: '#f6f8fa', color: '#23272f', fontSize: '15px', overflow: (showModal || showDeleteConfirm) ? 'hidden' : 'auto' }}>
      
      
            <AccessDeniedModal
        isOpen={accessModal.isOpen}
        onClose={() => setAccessModal({ isOpen: false })}
        title={accessModal.title}
        message={accessModal.message}
        page={accessModal.page}
        action={accessModal.action}
      />
      
      <div className="page-content" style={{ fontFamily: 'inherit', fontSize: '1.05rem' }}>
        <div className="container-fluid">
          <div id="table_height">

          {/* <div className="flex-grow-1" id="row_heading">

                        <h4 className="fw-bold mb-1 text-primary"><CommonIcon iconName="Tag" size={"28px"} />
                        Taxonomy Management</h4>
                        <p className="text-muted mb-0">Create, view, update, and delete items across multiple levels of your taxonomy.</p>
            </div> */}

            
 <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
              <div className="bg-primary text-white rounded p-2 me-3">
                <CommonIcon iconName="Tag" size={"24px"}  color="#fff" />
              </div>
              <div className="flex-grow-1">
                <h1 className="h3 mb-0 fw-bold"> Taxonomy Management</h1>
                <p className="text-muted mb-0 small">Create, view, update, and delete items across multiple levels of your taxonomy.</p>
              </div>
     {/* <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div> */}
            </div>  

          </div>


            <div id="app_err"></div>
          </div>
          <br/>
{hasPageAccess(pageName, "view") === false ? accessDenied() :loading ? (
            <PageLoader />
          ) : taxonomyState && Object.keys(taxonomyState).length > 0 ? (
            <div className="p-0" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
              <div className="bg-white border-bottom px-4 py-3" style={{ fontFamily: 'inherit', fontSize: '1.08rem', boxShadow: '0 2px 8px #e0e7ff1a' }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-4 py-2 px-2 rounded flex-grow-1" style={{ background: '#f5f7ff', borderRadius: 12, boxShadow: '0 1px 4px #e0e7ff' }}>
                    <span className="d-flex align-items-center gap-1 text-primary fw-semibold" style={{ fontSize: 16, fontWeight: 500, fontFamily: 'inherit' }}>
                      <CalendarClock size={20} className="me-1" />
                      Last updated <span className="fw-bold ms-1">{auditLog?.timestamp || '-'}</span>
                    </span>
                    <span className="d-flex align-items-center gap-1 text-info fw-semibold" style={{ fontSize: 16, fontWeight: 500, fontFamily: 'inherit' }}>
                      <User size={19} className="me-1" />
                      By <span className="fw-bold ms-1">{auditLog?.user || '-'}</span>
                    </span>


                    
                    <span className="d-flex align-items-center gap-1 text-success fw-semibold" style={{ fontSize: 16, fontWeight: 500, fontFamily: 'inherit' }}>
                      <CheckCircle2 size={19} className="me-1" />
                      Last Changes <span className="fw-bold ms-1">{auditLog?.taxonomyName ? auditLog.taxonomyName : '-'} {auditLog?.action ? auditLog.action : '-'}</span>
                    </span>
                    <button
                      className="btn btn-primary btn-sm ms-auto"
                        // style={{ fontWeight: 500, fontFamily: 'inherit', fontSize: '1.05rem', letterSpacing: '-0.2px' }}
                        onClick={() => window.location.href = '#/taxonomy-audit-log'}
                        >
                        Show Audit Log
                        </button>
                        </div>
                      </div>
                      </div>
                      {/* Sticky header for tabs and search (single sticky container) */}
                      <div className="bg-white border-bottom px-3" style={{ position: 'sticky', top: HEADER_OFFSET, zIndex: 50, boxShadow: '0 2px 4px -1px rgba(0,0,0,0.07), 0 4px 10px -2px rgba(0,0,0,0.05)' }}>
                        <ul className="nav nav-tabs border-0 align-items-center flex-nowrap mb-0" style={{ background: 'inherit' }}>
                          {['product', 'sales', 'territories'].map((tab) => (
                            <li key={tab} className="nav-item" style={{ display: 'flex' }}>
                              <button
                                className={`nav-link px-4 py-3 border-0 ${activeTab === tab ? 'active text-primary border-bottom border-primary' : 'text-muted'}`}
                                style={{ fontSize: '1.0rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                                onClick={() => setActiveTab(tab)}
                                type="button"
                              >
                                {tab === 'product' && <CommonIcon iconName="Tag" size={"20px"} />}
                                {tab === 'sales' && <CommonIcon iconName="DollarSign" size={"20px"} />}
                                {tab === 'territories' && <CommonIcon iconName="Earth" size={"20px"} />}
                                <span className="ms-1">{getTabLabel(tab)}</span>
                                <span
                                  className="ms-2"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 32,
                                    height: 24,
                                    padding: '0 10px',
                                    borderRadius: 16,
                                    background: activeTab === tab
                                      ? 'linear-gradient(90deg, #23272f 60%, #3b4252 100%)'
                                      : 'linear-gradient(90deg, #64748b 60%, #94a3b8 100%)',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    boxShadow: activeTab === tab
                                      ? '0 2px 8px #23272f33'
                                      : '0 1px 4px #64748b33',
                                    transition: 'background 0.2s, box-shadow 0.2s'
                                  }}
                                >
                                  {(taxonomyState[tab] || []).length}
                                </span>
                              </button>
                            </li>
                          ))}
                          {/* Search as last list item with auto-left push */}
                          <li className="ms-auto" style={{ minWidth: 300, display: 'flex', alignItems: 'center' }}>
                            <div className="position-relative w-100" style={{ maxWidth: 360 }}>
                              <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                              {searchTerm !== debouncedSearchTerm && (
                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Searching...</span>
                                  </div>
                                </div>
                              )}
                              <input
                                type="text"
                                className="form-control ps-5 pe-5"
                                placeholder="Search categories and tags (e.g., 'Summer accessories')"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ paddingRight: searchTerm !== debouncedSearchTerm ? '3rem' : '1rem' }}
                              />
                            </div>
                          </li>
                        </ul>
                      </div>
              <div className="row g-0">
              {/* Sidebar */}
                <div className="col-md-12 bg-white border-end">
                  <div className="p-3">
                    {/* Search */}
                    {/* <div className="position-relative mb-3">
                      <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                      {searchTerm !== debouncedSearchTerm && (
                        <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Searching...</span>
                          </div>
                        </div>
                      )}
                      <input
                        type="text"
                        className="form-control ps-5 pe-5"
                        placeholder="Search categories and tags (e.g., 'Summer accessories')"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ paddingRight: searchTerm !== debouncedSearchTerm ? '3rem' : '1rem' }}
                      />
                    </div> */}
                    {/* Stats */}
                    <div className="row text-center mb-3 d-none" style={{ gap: 0 }}>
                      <div className="col-4">
                        <div style={{
                          background: 'linear-gradient(135deg, #e0e7ff 0%, #f5f7ff 100%)',
                          borderRadius: 18,
                          boxShadow: '0 2px 12px #e0e7ff55',
                          padding: '1.2rem 0.5rem',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minHeight: 110
                        }}>
                          <ListChecks className="mb-2" size={28} style={{ color: '#2563eb' }} />
                          <div style={{ fontWeight: 700, color: '#2563eb', fontSize: 28, fontFamily: 'inherit', letterSpacing: '-1px' }}>{stats.totalCount}</div>
                          <div style={{ fontSize: 15, color: '#64748b', fontWeight: 500, marginTop: 2 }}>Total Categories</div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setHighlightUntagged(v => !v)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setHighlightUntagged(v => !v); }}
                          style={{
                            background: highlightUntagged
                              ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                              : 'linear-gradient(135deg, #fff7ed 0%, #fef6e4 100%)',
                            borderRadius: 18,
                            boxShadow: highlightUntagged
                              ? '0 2px 16px #fbbf2477'
                              : '0 2px 12px #fbbf2433',
                            padding: '1.2rem 0.5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minHeight: 110,
                            cursor: 'pointer',
                            outline: highlightUntagged ? '2px solid #f59e42' : 'none',
                            transition: 'all 0.2s'
                          }}
                          aria-pressed={highlightUntagged}
                          title="Highlight untagged items"
                        >
                          <Tag className="mb-2" size={28} style={{ color: '#f59e42' }} />
                          <div style={{ fontWeight: 700, color: '#f59e42', fontSize: 28, fontFamily: 'inherit', letterSpacing: '-1px' }}>{stats.untaggedCount}</div>
                          <div style={{ fontSize: 15, color: '#b0894a', fontWeight: 500, marginTop: 2 }}>Untagged Items</div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div style={{
                          background: 'linear-gradient(135deg, #f0fdf4 0%, #e0f2fe 100%)',
                          borderRadius: 18,
                          boxShadow: '0 2px 12px #10b98133',
                          padding: '1.2rem 0.5rem',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minHeight: 110
                        }}>
                          <SearchCheck className="mb-2" size={28} style={{ color: '#10b981' }} />
                          <div style={{ fontWeight: 700, color: '#10b981', fontSize: 28, fontFamily: 'inherit', letterSpacing: '-1px' }}>{debouncedSearchTerm ? stats.searchResults : 0}</div>
                          <div style={{ fontSize: 15, color: '#089981', fontWeight: 500, marginTop: 2 }}>Matching Results</div>
                          <div style={{ fontSize: 13, color: '#2563eb', fontWeight: 500, marginTop: 2 }}>{debouncedSearchTerm ? `${stats.searchResults} / ${stats.totalCount}` : `0 / ${stats.totalCount}`}</div>
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="d-flex gap-2 mb-3 justify-content-end d-none">
                      <button className="btn btn-primary btn-sm" onClick={handleExpandAll}>
                        Expand All
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleCollapseAll}>
                        Collapse All
                      </button>
                      {/* <button className="btn btn-primary btn-sm" onClick={handleAddTopLevel}>
                        <Plus size={16} className="me-1" />
                        Add Top Level Category
                      </button> */}
                    </div>
                  </div>
                 
                            <div
                            style={{
                                // Dynamically calculate maxHeight based on window height
                                maxHeight: `calc(${window.innerHeight ? window.innerHeight : 800}px - 300px)`,
                                overflowY: 'auto',
                                background: 'inherit',
                                borderRadius: 12
                              }}
                              >
                              <div
                                className="px-3 py-2 bg-light border-bottom"
                                style={{
                                fontFamily: 'inherit',
                                fontWeight: 500,
                                fontSize: '1.08rem',
                                letterSpacing: '-0.2px',
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                boxShadow: '0 2px 6px #e0e7ff22'
                                }}
                              >
                                <div className="d-flex align-items-center justify-content-between">
                                <small className="fw-bold text-muted" style={{ fontSize: '16px', fontFamily: 'inherit', letterSpacing: '-0.2px' }}>
                                  Taxonomy Hierarchy  
                                </small>
                                <div className="d-flex gap-2 ms-auto">
                                  <button className="btn btn-primary btn-sm" onClick={handleExpandAll}>
                                  Expand All
                                  </button>
                                  <button className="btn btn-primary btn-sm" onClick={handleCollapseAll}>
                                  Collapse All
                                  </button>
                                  <button
                                  className="btn btn-primary btn-sm"
                                  onClick={handleSave}
                                  disabled={!unsavedChanges || isSaving}
                                  style={{
                                    cursor: (!unsavedChanges || isSaving) ? 'not-allowed' : 'pointer'
                                  }}
                                  >
                                  {isSaving ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                                </div>
                              </div>
                              {treeStructure.length === 0 ? (
                                <div
                                className="text-center text-muted py-5"
                                style={{ fontSize: "1.15rem", fontWeight: 500 }}
                                >
                                <span>No Taxonomy Found.</span>
                                </div>
                              ) : (
                                <>
                                {/* Toggle Tree View Button */}
    {/* <div className="d-flex justify-content-end align-items-center px-3 py-2">
      <button
        className="btn btn-outline-primary btn-sm"
        onClick={() => setShowTree(prev => !prev)}
        style={{ minWidth: 110 }}
      >
        {showTree ? 'Hide Tree' : 'Show Tree'}
      </button>
    </div> */}

    {/* Example static row for preview */}
      <div
        className="d-flex align-items-center py-2 px-3 border-bottom position-relative"
        style={{ paddingLeft: "-126px", transition: "background 0.2s" }}
      >
        <button
        className="btn btn-link p-0 me-2 text-muted d-flex align-items-center justify-content-center"
        tabIndex={-1}
        onClick={() => setShowTree(prev => !prev)}
        >
        {showTree ? 
        <CommonIcon iconName="ChevronDown" size={"14px"} />
        :
        <CommonIcon iconName="ChevronRight" size={"14px"} />
        }
        </button>

        <div className="flex-grow-1 d-flex align-items-center" style={{ minHeight: "22px" }}>
        <div className="d-flex flex-column">
          <span className="me-2 d-flex align-items-center" style={{ fontSize: "15px", height: "18px" }}>
          {activeTab === 'product' && '  Product Categories'}
          {activeTab === 'sales' && '  Sales Channels'}
          {activeTab === 'territories' && '  Territories'}
          </span>
        </div>
        </div>

        <div className="d-flex align-items-center gap-2">
        <span className="badge bg-light text-dark" style={{ fontSize: '15px' }}>
          ({treeStructure.length})
        </span>
        <button
          className="btn btn-link p-0 text-success"
          onClick={handleAddTopLevel}
          style={{ width: '16px', height: '16px' }}
          title="Add Child"
        >
          <Plus size={14} />
        </button>
        <button
          className="btn btn-link p-0 text-danger"
          style={{ width: '16px', height: '16px', disabled: true, opacity: 0.8, cursor: 'not-allowed' }}
          title="Delete"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" /></svg>
        </button>
        <button
          className="btn btn-link p-0 text-muted"
          style={{ width: '16px', height: '16px', disabled: true, opacity: 0.8, cursor: 'not-allowed' }}
        >
          <Edit3 size={14} />
        </button>
        </div>
      </div>

      {/* Dynamic Rendering */}
    {showTree && (
      <div style={{ paddingLeft : "20px" }} id='tree_container'>
        {treeStructure
          .slice() // create a shallow copy to avoid mutating the original
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
          .map((item) => (
            <TreeNode
              key={item.id}
              item={item}
              children={
                item.children
                  ? item.children.slice().sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                  : []
              }
              expandedNodes={expandedNodes}
              onToggleNode={handleToggleNode}
              onEditItem={handleEditItem}
              onDeleteItem={handleDeleteItem}
              onAddChildItem={handleAddChildItem}
              searchTerm={debouncedSearchTerm}
              highlightUntagged={highlightUntagged}
            />
          ))}
      </div>
    )}
  </>
)}

                  </div>
                </div>
              </div>
              {/* Edit Modal */}
                      {showModal && selectedItem && (
                      <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg" style={{ maxWidth: 900 }}>
                        <div className="modal-content">
                          <div className="modal-header">
                          <h5 className="modal-title">{isAddMode ? 'Add Category' : 'Edit Category'}</h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowModal(false)}
                          ></button>
                          </div>
                          <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Parent Category</label>
                            {isAddMode ? (
                            <select
                              className="form-select"
                              value={selectedItem.parentId || ''}
                              onChange={e => {
                                setSelectedItem({ ...selectedItem, parentId: e.target.value || undefined });
                                //setUnsavedChanges(true);
                              }}
                            >
                              <option value="">Root (Top Level)</option>
                              {getCurrentTaxonomy()
                                .filter(item => item.level < selectedItem.level)
                                .map(parent => (
                                  <option key={parent.id} value={parent.id}>
                                    {parent.label} (Level {parent.level})
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <input
                              className="form-control"
                              value={(() => {
                                if (!selectedItem.parentId) return 'None (Top Level)';
                                const parent = getCurrentTaxonomy().find(i => i.id === selectedItem.parentId);
                                return parent ? `${parent.label} (Level ${parent.level})` : 'Unknown';
                              })()}
                              disabled
                            />
                          )}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Category Name</label>
                          <input
                            type="text"
                            className={`form-control${(isAddMode && !selectedItem.label.trim()) || validationErrors.label ? ' is-invalid' : ''}`}
                            value={selectedItem.label}
                            onChange={e => { 
                              // Format label as title case
                              const titleCase = (str) => {
                                return str.replace(/\w\S*/g, (txt) => 
                                  txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                                );
                              };
                              const newLabel = titleCase(e.target.value);
                              setSelectedItem({ ...selectedItem, label: newLabel }); 
                              // Validate
                              const errors = validateLabel(newLabel, selectedItem.parentId, selectedItem.id);
                              setValidationErrors(errors);
                              //setUnsavedChanges(true);
                            }}
                          />
                          {((isAddMode && !selectedItem.label.trim()) || validationErrors.label) && (
                            <div className="invalid-feedback">
                              {validationErrors.label || 'Category name cannot be empty.'}
                            </div>
                          )}
                        </div>
                              {activeTab === 'sales' && (
                                <div className="mb-3">
                                  <label className="form-label">Tags</label>
                                  <div className="row">
                                    {/* Left: Selected tags */}
                                    <div className="col-6">
                                      <div className="border rounded p-2" style={{ minHeight: 300, background: "#f8fafc" }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                          <span className="fw-semibold" style={{ fontSize: 14 }}>All Tags</span>
                                          <div className="position-relative" style={{ width: "180px" }}>
                                            <Search
                                              size={14}
                                              className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"
                                            />
                                            <input
                                              type="text"
                                              className="form-control form-control-sm ps-4"
                                              placeholder="Search tags..."
                                              value={selectedItem._tagSearch}
                                              onChange={e => setSelectedItem({ ...selectedItem, _tagSearch: e.target.value })}
                                              style={{ paddingLeft: "1.8rem" }}
                                            />
                                          </div>
                                        </div>
                                        <ul className="list-group list-group-flush" style={{ maxHeight: 300, overflowY: "auto" }}>
                                          {(() => {
                                            // Gather all unique tags from all sales categories
                                            const allTags = new Set();
                                            (taxonomyState.sales || []).forEach(cat =>
                                              (cat.tags || []).forEach(tag => allTags.add(tag.name))
                                            );
                                            // Add tags from current selectedItem if not present
                                            (selectedItem.tags || []).forEach(tag => allTags.add(tag.name));
                                            // Remove tags already selected in this category
                                            const availableTags = Array.from(allTags).filter(
                                              tagName => !(selectedItem.tags || []).some(t => t.name === tagName)
                                            );
                                            // Sort availableTags alphabetically (case-insensitive)
                                            // Apply search filter if user typed in the tag search box
                                            const searchTerm = (selectedItem._tagSearch || '').toLowerCase().trim();
                                            const filteredAvailable = searchTerm
                                              ? availableTags.filter(t => t.toLowerCase().includes(searchTerm))
                                              : availableTags;
                                            const sortedAvailableTags = filteredAvailable.slice().sort((a, b) =>
                                              a.localeCompare(b, undefined, { sensitivity: "base" })
                                            );

                                            return sortedAvailableTags.length
                                              ? sortedAvailableTags.map(tagName => (
                                                  <li
                                                    key={tagName}
                                                    className={`list-group-item py-1 px-2 d-flex align-items-center justify-content-between ${selectedItem._tagCandidate === tagName ? "" : ""}`}
                                                    style={{ cursor: "pointer", fontSize: 14 }}
                                                    onClick={() => setSelectedItem({ ...selectedItem, _tagCandidate: tagName, _tagError: "" })}
                                                  >
                                                    {/* Delete tag from ALL categories (not just current) */}
                                                    <span
                                                      style={{ cursor: "pointer", marginLeft: 8, color: "#dc3545" }}
                                                      title="Delete tag from all categories"
                                                      onClick={e => {
                                                        e.stopPropagation();
                                                        // Remove tag from all sales categories
                                                        setTaxonomyState(prev => {
                                                          const updatedSales = (prev.sales || []).map(cat => ({
                                                            ...cat,
                                                            tags: Array.isArray(cat.tags)
                                                              ? cat.tags.filter(t => t.name !== tagName)
                                                              : []
                                                          }));
                                                          // Also remove from selectedItem if present
                                                          let updatedSelected = { ...selectedItem };
                                                          if (Array.isArray(updatedSelected.tags)) {
                                                            updatedSelected.tags = updatedSelected.tags.filter(t => t.name !== tagName);
                                                          }
                                                          return {
                                                            ...prev,
                                                            sales: updatedSales
                                                          };
                                                        });
                                                        // Remove from selectedItem UI state
                                                        setSelectedItem(si => ({
                                                          ...si,
                                                          tags: Array.isArray(si.tags)
                                                            ? si.tags.filter(t => t.name !== tagName)
                                                            : [],
                                                          _tagError: ""
                                                        }));
                                                      }}
                                                    >
                                                      <CommonIcon iconName="Trash2" size={"13px"} /> &nbsp;
                                                    </span>

                                                    <span className="flex-grow-1 text-start">{tagName}</span>
                                                    
                                                    <span className="badge bg-light text-dark ms-2" style={{ fontSize: 11 }}>
                                                      {(() => {
                                                        // Show which category uses this tag
                                                        const usedCat = (taxonomyState.sales || []).find(cat =>
                                                          Array.isArray(cat.tags) && cat.tags.some(t => t.name === tagName)
                                                        );
                                                        return usedCat ? usedCat.label : "";
                                                      })()}
                                                    </span>
                                                  </li>
                                                ))
                                              : (
                                                <li className="list-group-item text-muted py-1 px-2" style={{ fontSize: 13 }}>No tags available.</li>
                                              );
                                             
                                          })()}
                                        </ul>
                                      </div>
                                    </div>
                                    
                                    {/* Middle: Move buttons */}
                                    {/* <div className="d-none col-1 d-flex flex-column align-items-center justify-content-center gap-0">
                                      <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        title="Add tag"
                                        disabled={
                                          !selectedItem._tagCandidate ||
                                          !selectedItem._tagCandidate.length ||
                                          selectedItem.tags?.some(t => t.name === selectedItem._tagCandidate)
                                        }
                                        onClick={() => {
                                          // Validation: tag can only be used in one category
                                          const allSales = taxonomyState.sales || [];
                                          const tagName = selectedItem._tagCandidate;
                                            const alreadyUsed = allSales.find(cat =>
                                            cat.id !== selectedItem.id &&
                                            Array.isArray(cat.tags) &&
                                            cat.tags.some(t => t.name.trim().toLowerCase() === tagName.trim().toLowerCase())
                                            );
                                          if (alreadyUsed) {
                                            setSelectedItem({
                                              ...selectedItem,
                                              _tagError: `Tag "${tagName}" already used in "${alreadyUsed.label}".`
                                            });
                                            return;
                                          }
                                          setSelectedItem({
                                            ...selectedItem,
                                            tags: [...(selectedItem.tags || []), { id: tagName, name: tagName }],
                                            _tagCandidate: "",
                                            _tagError: ""
                                          });
                                        }}
                                      >&lt;&lt;</button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm d-none"
                                        title="Remove tag"
                                        disabled={!selectedItem._tagRemoveCandidate}
                                        onClick={() => {
                                          setSelectedItem({
                                            ...selectedItem,
                                            tags: selectedItem.tags.filter(t => t.name !== selectedItem._tagRemoveCandidate),
                                            _tagRemoveCandidate: "",
                                            _tagError: ""
                                          });
                                        }}
                                      >&gt;&gt;</button>
                                    </div> */}
                                    {/* Right: All tags + add new */}

                                    <div className="col-6">
                                     <div className="border rounded p-2" style={{ minHeight: 350, background: "#f8fafc" }}>
                                        <div
  className="fw-semibold mb-2 d-flex align-items-center gap-2"
  style={{ fontSize: 14 }}
>
  <div className="d-flex align-items-start justify-content-between gap-3 mb-2" style={{ width: '100%' }}>
    <span className='fw-semibold' style={{ fontSize: 14, lineHeight: '28px' }}>Available Tags</span>
    <form
      className="d-flex align-items-center gap-2"
      onSubmit={e => {
        e.preventDefault();
        const tagName = (selectedItem._newTagName || "").trim();
        if (!tagName) return;

        // Check for duplicate in current category
        // Check for duplicate in current category (case-insensitive, but preserve original case)
        const existingTag = (selectedItem.tags || []).find(
          t => t.name.trim().toLowerCase() === tagName.trim().toLowerCase()
        );
        if (existingTag) {
          setSelectedItem({
            ...selectedItem,
            _tagError: `Tag "${existingTag.name}" already exists in this category (case-insensitive match).`
          });
          return;
        }

        // Validation: tag can only be used in one category
        const allSales = taxonomyState.sales || [];
        const alreadyUsed = allSales.find(cat =>
          cat.id !== selectedItem.id &&
          Array.isArray(cat.tags) &&
          cat.tags.some(t => t.name.toLowerCase() === tagName.toLowerCase())
        );
        
        if (alreadyUsed) {
          setSelectedItem({
            ...selectedItem,
            _tagError: `Tag "${tagName}" already used in "${alreadyUsed.label}".`
          });
          return;
        } else {
          setSelectedItem({
            ...selectedItem,
            tags: [...(selectedItem.tags || []), { id: tagName, name: tagName }],
            _newTagName: "",
            _tagError: ""
          });
        }
      
        
      }}
    >
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Add new tag"
        style={{ width: 140, fontSize: 13 }}
        value={selectedItem._newTagName || ""}
        onChange={e =>
          setSelectedItem({
            ...selectedItem,
            _newTagName: e.target.value,
            _tagError: ""
          })
        }
      />
      <button
        type="submit"
        className="btn btn-sm btn-primary px-3"
        style={{ fontSize: 13 }}
        
      >
        Add
      </button>
    </form>
  </div>
    </div>

    {selectedItem.tags && selectedItem.tags.length > 0 ? (
      <ul className="list-group list-group-flush">
        {selectedItem.tags.map((tag, idx) => (
      <li key={tag.id || tag.name}
        style={{
          cursor: "pointer",
          fontSize: 14
        }}
        className="list-group-item d-flex align-items-center justify-content-between py-1 px-2">
        <span>{tag.name}</span>
        <button
          style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
          type="button"
          onClick={() => {
                                                    setSelectedItem({
                                                      ...selectedItem,
                                                      tags: selectedItem.tags.filter((_, i) => i !== idx)
                                                    });
                                                  }}
                                                ><CommonIcon iconName="Trash2" size={"13px"} /></button>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <div className="text-muted" style={{ fontSize: 13 }}>No tags selected.</div>
                                        )}
                                     </div>
                                    </div>
                                    
                                  </div>
                                  {/* Error message */}
                                  {selectedItem._tagError && (
                                    <div className="alert alert-danger mt-2 py-1" style={{ fontSize: 13 }}>
                                      {selectedItem._tagError}
                                    </div>
                                  )}
                                </div>
                              )}
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => setShowModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={isAddMode && (!selectedItem.label.trim() || validationErrors.label)}
                          onClick={() => {
                            if (isAddMode && (!selectedItem.label.trim() || validationErrors.label)) return;
                            if (isAddMode) {
                              // Generate ID from label: "Luggage Tags" -> "luggage_tags"
                              const generateId = (label) => {
                                return label
                                  .toLowerCase()
                                  .replace(/[^a-z0-9\s]/g, '') // Remove special characters
                                  .replace(/\s+/g, '_') // Replace spaces with underscores
                                  .replace(/_+/g, '_') // Replace multiple underscores with single
                                  .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
                              };
                              let newId = generateId(selectedItem.label);
                              const existingIds = (taxonomyState[activeTab] || []).map(item => item.id);
                              // Ensure unique ID
                              let counter = 1;
                              let uniqueId = newId;
                              while (existingIds.includes(uniqueId)) {
                                uniqueId = `${newId}_${counter}`;
                                counter++;
                              }
                              const itemToAdd = { ...selectedItem, id: uniqueId };
                              setTaxonomyState(prev => {
                                const arr = [...(prev[activeTab] || [])];
                                arr.push(itemToAdd);
                                return { ...prev, [activeTab]: arr };
                              });
                            } else {
                              setTaxonomyState(prev => {
                                const arr = [...(prev[activeTab] || [])];
                                const idx = arr.findIndex(i => i.id === selectedItem.id);
                                if (idx !== -1) arr[idx] = { ...selectedItem };
                                return { ...prev, [activeTab]: arr };
                              });
                            }
                            setShowModal(false);
                            setUnsavedChanges(true);
                          }}
                        >
                          {isAddMode ? 'Add Category' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Delete confirmation modal */}
              {showDeleteConfirm && itemToDelete && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Confirm Delete</h5>
                        <button type="button" className="btn-close" onClick={cancelDelete}></button>
                      </div>
                      <div className="modal-body">
                        Are you sure you want to delete <b>{itemToDelete.label}</b> and all its subcategories?
                      </div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-primary" onClick={cancelDelete}>Cancel</button>
                        <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-muted py-5" style={{ fontSize: '1.15rem', fontWeight: 500 }}>
              <span>No Taxonomy Data Available.</span>
            </div>
          )}
        </div>
      </div>



    </div>

    
  );
};

export default TaxonomyManager;